import { ContainerComponent, wrap, today } from 'AppReact';
import { BorderlessKeyInput, ClickableIcon, Icon, SecondaryTextXs, UnderlinedTabButton } from 'atoms';
import { CalendarSvg, MagnifyingGlassSvg, Person } from 'components/icons';
import { AssignWorkerRow, Calendar } from 'containers';
import { OutlinedButton, SolidButton } from 'molecules';
import React from 'react';
import {
  AbsoluteLeftTopWrapper,
  DropdownOuterWrapper, FitRelativeWrapper,
  FlexCenterWrapper, FullWidthJustifyEndWrapper, Mr2, Mr3,
  Mr4, Mx2, Mx4, Pb4, Pl4, Py2, CustomBorderWrapper,
  StickyTopWrapper, Width350
} from 'wrappers';
import { getAdminList, getTechList, getVendorList } from '../assignWorkerDropdown/utils';
import Controller from './controller';
import internalState from './internalState';
import { ContentWrapper } from './wrappers';

const popupRef = React.createRef();

export const definition = {
  name: 'MultiSelectHeader',
  controller: Controller,
  props: internalState,
  initialProps: ({ selectedItems, selectedCategories, isOpenAssignDropdown, isOpenScheduleDropdown, assignedTechId, assignedVendorId, admins, techs, vendors, hideAdmins, hideTechs, hideVendors }) => ({
    selectedItems,
    selectedCategories,
    isOpenAssignDropdown,
    isOpenScheduleDropdown,
    assignedTechId,
    assignedVendorId,
    admins,
    techs,
    vendors,
    hideAdmins,
    hideTechs,
    hideVendors,
    selectedTab: !hideTechs ? 'techs' : !hideVendors ? 'vendors' : !hideAdmins ? 'admins' : ''
  }),
  elements: [
    {
      wrapper: FlexCenterWrapper,
      name: 'HeaderWrapper',
      elements: [
        {
          component: wrap(ClickableIcon, Mx2),
          name: 'Icon',
          bindings: () => ({}),
          displayValue: () => <i className="fas fa-minus-square" />
        },
        {
          component: wrap(wrap(SecondaryTextXs, Mx2), Width350),
          name: 'Label',
          displayValue: ({ selectedItems, selectedCategories }) => `${(selectedItems?.length || selectedCategories?.length) ?? '0'} Selected`
        },
        {
          wrapper: FullWidthJustifyEndWrapper,
          name: 'ButtonsWrapper',
          elements: [
            {
              wrapper: FitRelativeWrapper,
              name: 'AssignButtonWrapper',
              renderIf: ({ selectedItems }) => !selectedItems?.find((i) => i.is_complete),
              elements: [
                {
                  component: wrap(OutlinedButton, Mr2),
                  name: 'AssignButton',
                  bindings: ({ isOpenAssignDropdown, selectedItems }) => ({
                    isOpenAssignDropdown,
                    selectedItems,
                    icon: Person({ width: 14, height: 14 }),
                    title: selectedItems?.find((i) => i.is_assigned) ? 'Re-Assign' : 'Assign'
                  })
                },
                {
                  wrapper: AbsoluteLeftTopWrapper,
                  name: 'DropdownWrapper',
                  renderIf: ({ isOpenAssignDropdown }) => isOpenAssignDropdown,
                  displayState: () => ({ background: '#FFFFFF' }),
                  ref: popupRef,
                  elements: [
                    {
                      wrapper: DropdownOuterWrapper,
                      name: 'ContentWrapper',
                      renderIf: ({ isOpenAssignDropdown }) => isOpenAssignDropdown,
                      elements: [
                        {
                          wrapper: ContentWrapper,
                          name: 'Content',
                          elements: [
                            {
                              wrapper: wrap(Py2, StickyTopWrapper),
                              name: 'Tabs',
                              elements: [
                                {
                                  wrapper: wrap(wrap(FlexCenterWrapper, Pl4), Py2),
                                  name: 'TabLabelWrapper',
                                  elements: [
                                    {
                                      component: wrap(UnderlinedTabButton, Mr3),
                                      name: 'Techs',
                                      displayValue: () => 'Techs',
                                      renderIf: ({ hideTechs }) => !hideTechs,
                                      displayState: ({ selectedTab }) => ({ selected: selectedTab === 'techs' }),
                                      bindings: () => ({})
                                    },
                                    {
                                      component: wrap(UnderlinedTabButton, Mr3),
                                      name: 'Vendors',
                                      displayValue: () => 'Vendors',
                                      renderIf: ({ hideVendors }) => !hideVendors,
                                      displayState: ({ selectedTab }) => ({ selected: selectedTab === 'vendors' }),
                                      bindings: () => ({})
                                    },
                                    {
                                      component: UnderlinedTabButton,
                                      name: 'Admins',
                                      displayValue: () => 'Admins',
                                      renderIf: ({ hideAdmins }) => !hideAdmins,
                                      displayState: ({ selectedTab }) => ({ selected: selectedTab === 'admins' }),
                                      bindings: () => ({})
                                    }
                                  ]
                                },
                                {
                                  wrapper: CustomBorderWrapper,
                                  name: 'BackgroundWrapper',
                                  displayState: () => ({ border: '1px solid #E3E3E3', background: '#F7F7F7' }),
                                  elements: [
                                    {
                                      wrapper: FlexCenterWrapper,
                                      name: 'LeftSide',
                                      elements: [
                                        {
                                          component: BorderlessKeyInput,
                                          name: 'Search',
                                          renderIf: ({ assignedTechId, assignedVendorId, assignedAdminId }) => !assignedTechId && !assignedVendorId && !assignedAdminId,
                                          bindings: ({ searchVal }) => ({ value: searchVal, placeholder: 'Search...' }),
                                          displayState: () => ({ background: '#F7F7F7' })
                                        },
                                        {
                                          component: wrap(Icon, Mr4),
                                          name: 'MagnifyingGlassIcon',
                                          displayValue: () => MagnifyingGlassSvg({ color: window?.settings?.secondary_color ?? '#04333b' })
                                        }
                                      ]
                                    }
                                  ]
                                }
                              ]
                            },
                            {
                              component: wrap(wrap(SecondaryTextXs, Mx4), Pb4),
                              name: 'NoTechAvailable',
                              renderIf: ({ searchVal, techs, selectedTab }) => getTechList({ searchVal, techs }).length === 0 && selectedTab === 'techs',
                              displayValue: () => 'No technician available'
                            },
                            {
                              component: AssignWorkerRow,
                              name: 'AssignTech',
                              renderIf: ({ selectedTab }) => selectedTab === 'techs',
                              isListOf: { tech: getTechList },
                              bindings: ({ tech, assignedTechId, selectedItems }) => ({ selectedItems, tech, image: tech.image, name: tech.name, assignments: tech.stats.assigned, completion_time: tech.stats.completion_time, rating: tech.stats.rating, id: tech.id, assignedId: assignedTechId })
                            },
                            {
                              component: wrap(wrap(SecondaryTextXs, Mx4), Pb4),
                              name: 'NoVendorsAvailable',
                              renderIf: ({ selectedTab, searchVal, vendors }) => getVendorList({ searchVal, vendors }).length === 0 && selectedTab === 'vendors',
                              displayValue: () => 'No vendor available'
                            },
                            {
                              component: AssignWorkerRow,
                              name: 'AssignVendor',
                              renderIf: ({ selectedTab }) => selectedTab === 'vendors',
                              isListOf: { vendor: getVendorList },
                              bindings: ({ vendor, selectedItems, assignedVendorId }) => ({ selectedItems, vendor, image: vendor?.vendor_url ?? vendor?.attachment, name: vendor.name, assignments: vendor.stats?.assigned, completion_time: vendor.stats?.completion_time, rating: vendor.stats?.rating, id: vendor.id, assignedId: assignedVendorId })
                            },
                            {
                              component: wrap(wrap(SecondaryTextXs, Mx4), Pb4),
                              name: 'NoAdminsAvailable',
                              renderIf: ({ selectedTab, searchVal, admins }) => getAdminList({ searchVal, admins })?.length === 0 && selectedTab === 'admins',
                              displayValue: () => 'No admin available'
                            },
                            {
                              component: AssignWorkerRow,
                              name: 'AssignAdmin',
                              renderIf: ({ selectedTab }) => selectedTab === 'admins',
                              isListOf: { admin: getAdminList },
                              bindings: ({ selectedItems, admin }) => ({
                                selectedItems,
                                admin,
                                image: admin.image,
                                name: admin.name,
                                assignments: 0,
                                completion_time: 0,
                                rating: 0,
                                id: admin.id,
                                role: admin.is_super_admin ? 'Super Admin' : admin.roles.length > 0 ? admin.roles.join(', ') : 'No role',
                                displayButton: true
                              })
                            }
                          ]
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              wrapper: FitRelativeWrapper,
              name: 'ScheduleButtonWrapper',
              renderIf: ({ selectedItems }) => !selectedItems?.find((i) => i.is_complete),
              elements: [
                {
                  component: wrap(OutlinedButton, Mr2),
                  name: 'ScheduleButton',
                  bindings: ({ isOpenScheduleDropdown, selectedItems }) => ({
                    isOpenScheduleDropdown,
                    icon: CalendarSvg({ width: 14, height: 14 }),
                    title: selectedItems?.find((i) => i.is_scheduled) ? 'Re-Schedule' : 'Schedule'
                  })
                },
                {
                  wrapper: AbsoluteLeftTopWrapper,
                  name: 'DropdownWrapper',
                  renderIf: ({ isOpenScheduleDropdown }) => isOpenScheduleDropdown,
                  displayState: () => ({ top: '50px', left: '-120px' }),
                  ref: popupRef,
                  elements: [
                    {
                      component: Calendar,
                      name: 'Calendar',
                      bindings: ({ selectedDate, selectedItems }) => {
                        const month = selectedDate?.getMonth?.() || 5;
                        const year = selectedDate?.getFullYear?.() || 2020;
                        return { selectedDate, month, year, selectedItems, disabledBefore: today() };
                      }
                    }
                  ]
                }
              ]
            },
            {
              component: wrap(SolidButton, Mr2),
              name: 'CompleteItemsButton',
              renderIf: ({ selectedCategories }) => selectedCategories.length === 0,
              bindings: ({ selectedItems }) => ({
                selectedItems,
                title: selectedItems?.find((i) => i.is_complete) ? 'Reopen' : 'Complete Tasks'
              })
            },
            {
              wrapper: wrap(FlexCenterWrapper, Mr2),
              name: 'ColumnButtonsWrapper',
              renderIf: ({ selectedCategories }) => selectedCategories.length !== 0,
              elements: [
                {
                  component: wrap(OutlinedButton, Mr2),
                  name: 'ReopenColumnButton',
                  bindings: ({ selectedItems }) => ({
                    selectedItems,
                    title: 'Reopen Column(s)'
                  })
                },
                {
                  component: wrap(SolidButton, Mr2),
                  name: 'CompleteColumnButton',
                  bindings: ({ selectedItems }) => ({
                    selectedItems,
                    title: 'Complete Column(s)'
                  })
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

export default ContainerComponent(definition);
